import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Input, Button, Switch, Select } from 'antd';
import DetailModal from 'components/Basic/DetailModal';
import { ArrowForward, ArrowDropDown, EmojiEvents } from '@material-ui/icons';
import toast from 'components/Basic/Toast';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import { Img } from 'react-image';
import { getImageLink } from 'utilities/common';
import {
  useCreatePrizesMutation,
  useUpdatePrizesMutation,
} from 'core/worldCup/prizes/PrizesService';
import { Prizes } from 'core/worldCup/prizes/types';
import { useLazyGetSponsorsSimpleListQuery } from 'core/worldCup/sponsors/SponsorsService';
import { setSponsorsData } from 'core/worldCup/sponsors/SponsorsSlice';
import { useLazyGetPrizeTypesSimpleListQuery } from 'core/worldCup/prizeTypes/PrizeTypesService';
import { setPrizeTypesData } from 'core/worldCup/prizeTypes/PrizeTypesSlice';
import TextArea from 'antd/es/input/TextArea';
import { sanitizeRule } from 'utilities/sanitize';
import { VALID_IMAGE_FORMATS } from 'utilities/constants';
import { validateImageBytes } from 'utilities/validateImageFile';
import { getApiErrorMessage } from 'utilities/getApiErrorMessages';

type PrizeFormData = {
  sponsorId?: number;
  rewardTypeId: number;
  name: string;
  description: string;
  quantity: number;
  hasAmericanVisa: boolean;
  image: string;
};

type PrizesFormModalType = {
  mode: 'modal' | 'fullscreen';
  isOpenModal: boolean;
  prize: Prizes | null;
  onCloseModal: () => void;
  onSaveSuccess: () => void;
  campaignId?: number | null;
};

const PrizesFormModal: React.FC<PrizesFormModalType> = ({
  mode,
  isOpenModal,
  prize,
  onCloseModal,
  onSaveSuccess,
  campaignId,
}) => {
  const [form] = Form.useForm<PrizeFormData>();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const fileRef = useRef<HTMLInputElement>(null);
  const isModal = mode === 'modal';

  const [fileSrc, setFileSrc] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [dimensions, setDimensions] = useState({ type: '', width: 0, height: 0 });

  const [createPrize, { isLoading: isCreating }] = useCreatePrizesMutation();
  const [updatePrize, { isLoading: isUpdating }] = useUpdatePrizesMutation();

  const [getSponsorsSimpleList] = useLazyGetSponsorsSimpleListQuery();
  const sponsors = useAppSelector(state => state.sponsorsReducer).list.data || [];

  const [getPrizeTypesSimpleList] = useLazyGetPrizeTypesSimpleListQuery();
  const prizeTypes = useAppSelector(state => state.prizeTypesReducer).list.data || [];

  const selectClassName = isModal ? 'select_modal' : 'select_form';
  const textareaClassName = isModal ? 'textarea_modal' : 'textarea_form';

  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    if (isOpenModal) {
      if (prize) {
        form.setFieldsValue({
          image: prize.image,
          sponsorId: prize.sponsorId || undefined,
          rewardTypeId: prize.rewardTypeId || undefined,
          name: prize.name || '',
          description: prize.description || undefined,
          quantity: prize.quantity || undefined,
          hasAmericanVisa: prize.hasAmericanVisa ?? false,
        });
        setFileSrc(getImageLink(prize.image));
        setImageFile(null);
        setDimensions({ type: '', width: 0, height: 0 });
      } else {
        form.resetFields();
        setFileSrc(null);
        setImageFile(null);
        setDimensions({ type: '', width: 0, height: 0 });
      }
    }
  }, [isOpenModal, prize, form]);

  const getInitialData = async () => {
    const queryString = 'isActive=true';

    try {
      const res = await getSponsorsSimpleList({ queryString }).unwrap();
      dispatch(setSponsorsData({ list: res }));
    } catch (error) {
      console.error('Error fetching sponsors list:', error);
    }

    try {
      const res = await getPrizeTypesSimpleList({ queryString }).unwrap();
      dispatch(setPrizeTypesData({ list: res }));
    } catch (error) {
      console.error('Error fetching prize types list:', error);
    }
  };


  const openChooseFile = () => {
    fileRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error({ title: intl.formatMessage({ id: 'Image size should be less than 2MB' }) });
      return;
    }
    if (!VALID_IMAGE_FORMATS.includes(file.type)) {
      toast.error({ title: intl.formatMessage({ id: 'Incorrect file type' }) });
      return;
    }
    const isValidImage = await validateImageBytes(file);
    if (!isValidImage) {
      toast.error({ title: intl.formatMessage({ id: 'Incorrect file type' }) });
      return;
    }

    setImageFile(file);
    form.setFieldsValue({ image: file.name });

    const reader = new FileReader();
    reader.onload = () => {
        const img = new Image();
        img.onload = () => {
            setDimensions({
                width: img.width,
                height: img.height,
                type: file.type,
            });
        };
        const result = reader.result as string;
        img.src = result;
        setFileSrc(result);
        form.validateFields(['image']);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleFinish = async (values: PrizeFormData) => {
    const formData = new FormData();
    if (values.sponsorId) {
      formData.append('sponsorId', values.sponsorId.toString());
    }
    if(values.rewardTypeId) {
      formData.append('rewardTypeId', values.rewardTypeId.toString());
    }
    formData.append('name', values.name.trim());
    if (values.description) {
      formData.append('description', values.description);
    }
    formData.append('quantity', values.quantity ? values.quantity.toString() : '');
    if (imageFile) {
      formData.append('image', imageFile);
    }
    formData.append('hasAmericanVisa', values.hasAmericanVisa ? 'true' : 'false');
    if (campaignId) {
      formData.append('campaignId', String(campaignId));
    }
    try {
      if (prize && prize.id) {
        if(!imageFile) {
          formData.append('image', prize.image ? prize.image : '');
        }
        await updatePrize({ id: prize.id, data: formData }).unwrap();
        toast.success({ title: intl.formatMessage({ id: 'Prize is updated successfully!' }) });
      } else {
        await createPrize(formData).unwrap();
        toast.success({ title: intl.formatMessage({ id: 'Prize is created successfully!' }) });
      }

      onCloseModal();
      onSaveSuccess();

    } catch (e: any) {
      console.error('Prize save error:', e);
      const defaultMsg = intl.formatMessage({ id: prize?.id ? 'Updating prize is failure!' : 'Creating prize is failure!' });
      toast.error({ title: getApiErrorMessage(e, intl.locale, defaultMsg) });
    }
  };

  const isEditMode = prize && prize.id;

  const renderFormContent = () => (
    <Form
      form={form}
      className={isModal ? 'basic_info_form' : 'prizes_form'}
      onFinish={handleFinish}
      initialValues={{ image: null, name: null, description: null, sponsorId: undefined, rewardTypeId: undefined, hasAmericanVisa: false }}
    >
      {isModal ? (
        <>
          <div className="prizes_input">
            <Form.Item
              name="image"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!fileSrc && !prize?.image) {
                      return Promise.reject(new Error(intl.formatMessage({ id: 'Image is required' })));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <div className="photo_picker_wrapper">
                <div className="photo_info">
                  <p className="title"><FormattedMessage id="Prize image" /></p>
                  {dimensions.type && <p className="description">
                    Archivo {dimensions.type} de {dimensions.width}px por {dimensions.height}px
                  </p>}
                  <Button className="select_photo_btn" onClick={openChooseFile}>
                    <FormattedMessage id="Select File" />
                  </Button>
                </div>
                <div className="photo_area" onClick={openChooseFile}>
                  <Img src={fileSrc || ''} alt="Prize logo" />
                </div>
                <input type="file" className="file_input" accept="image/*" ref={fileRef} onChange={handleFileChange} hidden />
              </div>
            </Form.Item>
          </div>
          <div className="prizes_input fields_row">
            <div className="field_col">
              <p className="title"><FormattedMessage id="Sponsor" /></p>
              <Form.Item name="sponsorId" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                <Select
                  className="select_modal"
                  suffixIcon={<ArrowDropDown />}
                  id="sponsor_id_filter"
                  dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                >
                  {sponsors &&
                    sponsors.map((item: any) => (
                      <Select.Option value={item.id} key={item.id}>
                        {item.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </div>
            <div className="field_col">
              <p className="title"><FormattedMessage id="Prize type" /></p>
              <Form.Item name="rewardTypeId" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                <Select
                  className="select_modal"
                  suffixIcon={<ArrowDropDown />}
                  id="prize_type_id_filter"
                  dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                >
                  {prizeTypes &&
                    prizeTypes.map((item: any) => (
                      <Select.Option value={item.id} key={item.id}>
                        {item.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </div>
          </div>
          <div className="prizes_input fields_row">
            <div className="field_col">
              <p className="title"><FormattedMessage id="Name" /></p>
              <Form.Item name="name" rules={[{ required: true, message: intl.formatMessage({ id: 'Name is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
                <Input placeholder={intl.formatMessage({ id: 'Name' })} maxLength={200} />
              </Form.Item>
            </div>
            <div className="field_col">
              <p className="title"><FormattedMessage id="Prize quantity" /></p>
              <Form.Item name="quantity" rules={[{ required: true, message: intl.formatMessage({ id: 'Quantity is required!' }) }]}>
                <Input type="number" min={1} placeholder={intl.formatMessage({ id: 'Prize quantity' })} maxLength={10} />
              </Form.Item>
            </div>
          </div>
          <div className="prizes_input fields_row">
            <div className="field_col visa_switch_col">
              <p className="title"><FormattedMessage id="Requires VISA" /></p>
              <Form.Item name="hasAmericanVisa" valuePropName="checked">
                <Switch />
              </Form.Item>
              <p className="switch_hint"><FormattedMessage id="Requires VISA hint" /></p>
            </div>
          </div>
          <div className="prizes_input">
            <p className="title"><FormattedMessage id="Description" /></p>
            <Form.Item name="description" rules={[sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
              <TextArea rows={4} className="textarea_modal" placeholder={intl.formatMessage({ id: 'Description' })} maxLength={200} />
            </Form.Item>
          </div>
          <div className={isEditMode ? 'side_btns' : 'side_btns_camp'}>
            <div className="btn_wrapper">
              <Button className="save_sar_btn" htmlType="submit" loading={isCreating || isUpdating}>
                <FormattedMessage id={isEditMode ? 'Save changes' : 'Save'} />
                <ArrowForward />
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="prizes_form_fields">
            <div className="prizes_form_row">
              <Form.Item
                name="image"
                rules={[
                  {
                    validator: async () => {
                      if (!fileSrc && !prize?.image) {
                        return Promise.reject(new Error(intl.formatMessage({ id: 'Image is required' })));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <div className="photo_picker_wrapper">
                  <div className="photo_info">
                    <p className="title"><FormattedMessage id="Prize image" /></p>
                    {dimensions.type && <p className="description">
                      Archivo {dimensions.type} de {dimensions.width}px por {dimensions.height}px
                    </p>}
                    <Button className="select_photo_btn" onClick={openChooseFile}>
                      <FormattedMessage id="Select File" />
                    </Button>
                  </div>
                  <div className="photo_area" onClick={openChooseFile}>
                    <Img src={fileSrc || ''} alt="Prize logo" />
                  </div>
                  <input type="file" className="file_input" accept="image/*" ref={fileRef} onChange={handleFileChange} hidden />
                </div>
              </Form.Item>
            </div>
            <div className="prizes_form_row two_columns">
              <div className="prizes_field">
                <p className="title"><FormattedMessage id="Sponsor" /></p>
                <Form.Item name="sponsorId" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                  <Select
                    className="select_form"
                    suffixIcon={<ArrowDropDown />}
                    dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                  >
                    {sponsors &&
                      sponsors.map((item: any) => (
                        <Select.Option value={item.id} key={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </div>
              <div className="prizes_field">
                <p className="title"><FormattedMessage id="Prize type" /></p>
                <Form.Item name="rewardTypeId" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
                  <Select
                    className="select_form"
                    suffixIcon={<ArrowDropDown />}
                    dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
                  >
                    {prizeTypes &&
                      prizeTypes.map((item: any) => (
                        <Select.Option value={item.id} key={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </div>
            </div>
            <div className="prizes_form_row two_columns">
              <div className="prizes_field">
                <p className="title"><FormattedMessage id="Name" /></p>
                <Form.Item name="name" rules={[{ required: true, message: intl.formatMessage({ id: 'Name is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
                  <Input placeholder={intl.formatMessage({ id: 'Name' })} maxLength={200} />
                </Form.Item>
              </div>
              <div className="prizes_field">
                <p className="title"><FormattedMessage id="Prize quantity" /></p>
                <Form.Item name="quantity" rules={[{ required: true, message: intl.formatMessage({ id: 'Quantity is required!' }) }]}>
                  <Input type="number" min={1} placeholder={intl.formatMessage({ id: 'Prize quantity' })} maxLength={10} />
                </Form.Item>
              </div>
            </div>
            <div className="prizes_form_row">
              <div className="prizes_field visa_switch_col">
                <p className="title"><FormattedMessage id="Requires VISA" /></p>
                <Form.Item name="hasAmericanVisa" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <p className="switch_hint"><FormattedMessage id="Requires VISA hint" /></p>
              </div>
            </div>
            <div className="prizes_form_row">
              <div className="prizes_field">
                <p className="title"><FormattedMessage id="Description" /></p>
                <Form.Item name="description">
                  <TextArea rows={4} className="textarea_form" placeholder={intl.formatMessage({ id: 'Description' })} maxLength={200} />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="prizes_form_actions">
            <div className="btn_wrapper">
              <Button className="save_btn" htmlType="submit" loading={isCreating || isUpdating}>
                <FormattedMessage id={isEditMode ? 'Save changes' : 'Save'} />
                <ArrowForward />
              </Button>
              <Button className="cancel_btn" onClick={onCloseModal}>
                <FormattedMessage id="Cancel" />
              </Button>
            </div>
          </div>
        </>
      )}
    </Form>
  );

  if (isModal) {
    return (
      <DetailModal
        className="prizes_view_modal"
        visible={isOpenModal}
        width={550}
        title={<FormattedMessage id={isEditMode ? 'Edit prize' : 'New prize'} />}
        onClose={onCloseModal}
        sections={[
          {
            icon: <EmojiEvents />,
            title: <FormattedMessage id="Prize information" />,
            children: (
              <div className="new_prizes_form_wrapper">
                {renderFormContent()}
              </div>
            ),
          },
        ]}
      />
    );
  }

  if (!isOpenModal) return null;

  return (
    <div className="prizes_form_fullscreen">
      <div className="prizes_form_wrapper">
        <div className="prizes_form_header">
          <h3><FormattedMessage id={isEditMode ? 'Edit prize' : 'New prize'} /></h3>
        </div>
        {renderFormContent()}
      </div>
    </div>
  );
};

export default PrizesFormModal;
